import { DatePicker } from "@mui/x-date-pickers"
import { Box, Divider, FormControl, FormControlLabel, FormHelperText, Grid, InputAdornment, Radio, RadioGroup, TextField, Tooltip, Typography } from "@mui/material"
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers"
import { useFixRuleForm } from "hooks/fixRules/useFixRuleForm"
import { useTranslation } from "react-i18next"
import { nameValidation } from "utils/Validations"
import { FixRuleDays } from "shared/ui/number/FixRuleDays"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import InfoIcon from '@mui/icons-material/Info';
import dayjs from "dayjs"

interface Props {
    form: ReturnType<typeof useFixRuleForm>
    mode: 'add' | 'edit' | 'delete' | 'info'
}

export const FixedRuleForm = ({ form, mode }: Props) => {
    const { t } = useTranslation()
    const { existName, form: formState } = form
    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    id='name'
                    fullWidth
                    label={t('settings.fixed_rules.dialog.inputs.name')}
                    value={formState.name}
                    autoComplete='off'
                    onChange={(e) => {
                        if (nameValidation(e.target.value)) {
                            form.update('name', e.target.value)
                            existName(e.target.value)
                        }
                    }}
                    required
                    error={mode !== 'info' && existName(formState.name)}
                    slotProps={{
                        htmlInput: { maxLength: 100 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.name.length}/100`}</InputAdornment>,
                        },
                    }}
                    disabled={mode === 'info'}
                />
                <FormHelperText sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 1.75 }}>
                    <Box component={'span'} color={(theme) => theme.palette.error.main}>{mode !== 'info' && existName(formState.name) ? t('settings.fixed_rules.dialog.inputs.exist_name') : ''}</Box>
                </FormHelperText>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <TextField
                    id="filled-textarea"
                    fullWidth
                    label={t('settings.fixed_rules.dialog.inputs.description')}
                    value={formState.description}
                    autoComplete='off'
                    multiline
                    rows={2}
                    slotProps={{
                        htmlInput: { maxLength: 255 },
                        input: {
                            endAdornment: <InputAdornment position="end">{`${formState.description.length}/255`}</InputAdornment>,
                        },
                    }}
                    onChange={(e) => form.update('description', e.target.value)}
                    disabled={mode === 'info'}
                />
            </Grid>
            <Box sx={{ width: '100%' }}>
                <Divider />
            </Box>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth required>
                    <Typography>{t("settings.fixed_rules.dialog.inputs.type_payment_period")}</Typography>
                    <RadioGroup
                        aria-labelledby='type-period-radio-group'
                        name='type-period-radio-group'
                        row
                        value={formState.type_payment_period}
                        onChange={(e) => form.update('type_payment_period', e.target.value)}
                        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <FormControlLabel
                            value={'day'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.days')}
                            disabled={mode === 'info'}
                        />
                        <FormControlLabel
                            value={'bimonthly'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.bimonthly')}
                            disabled={mode === 'info'}
                        />
                        <FormControlLabel
                            value={'monthly'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.monthly')}
                            disabled={mode === 'info'}
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id='numberDays'
                    fullWidth
                    label={t('settings.fixed_rules.dialog.inputs.days')}
                    placeholder='0'
                    onChange={(e) => form.update('days', parseInt(e.target.value))}
                    value={formState.days}
                    autoComplete='off'
                    disabled={formState.type_payment_period === 'bimonthly' || formState.type_payment_period === 'monthly' || mode === 'info'}
                    name='numberDays'
                    slotProps={{
                        input: {
                            inputComponent: FixRuleDays,
                        }
                    }}
                    required
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                    <DatePicker
                        key={'date_reference'}
                        label={t('settings.fixed_rules.dialog.inputs.date_reference')}
                        onChange={(newValue) => form.update('date_reference', newValue)}
                        value={formState.date_reference}
                        slotProps={{
                            textField: () => ({
                                required: true,
                                fullWidth: true,
                            }),
                        }}
                        format='DD/MM/YYYY'
                        disabled={formState.type_payment_period === 'bimonthly' || formState.type_payment_period === 'monthly' || mode === 'info'}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <TextField
                    id='numberDaysReference'
                    name='numberDaysReference'
                    fullWidth
                    label={t('settings.fixed_rules.dialog.inputs.day_reference')}
                    onChange={(e) => form.update('day_reference', parseInt(e.target.value))}
                    value={formState.day_reference}
                    autoComplete='off'
                    disabled={formState.type_payment_period === 'day' || mode === 'info'}
                    placeholder={`(min 1 - max ${formState.type_payment_period === 'bimonthly' ? 15 : 31})`}
                    error={!form.validateDaysReference(formState.day_reference)}
                    slotProps={{
                        input: {
                            endAdornment: !form.validateDaysReference(formState.day_reference) && <InputAdornment position="end" style={{ cursor: 'pointer' }} color="error">
                                <Tooltip title={formState.type_payment_period === 'bimonthly' ? "El dia tiene que estar entre 1 y 15" : "El dia tiene que estar entre 1 y 31"} arrow>
                                    <InfoIcon color="error" />
                                </Tooltip>
                            </InputAdornment>
                        }
                    }}
                    required
                />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6, xl: 6 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
                    <TimePicker
                        key={'day_division_hours'}
                        label={t('settings.fixed_rules.dialog.inputs.day_division_hours')}
                        onChange={(newValue) => form.update('day_division_hours', newValue)}
                        value={formState.day_division_hours ? dayjs(formState.day_division_hours) : null}
                        slotProps={{
                            textField: {
                                required: true,
                                fullWidth: true,
                            },
                        }}
                        minutesStep={1}
                        timeSteps={{ hours: 1, minutes: 1 }}
                        disabled={mode === 'info'}
                    />
                </LocalizationProvider>
            </Grid>
            <Box sx={{ width: '100%' }}>
                <Divider />
            </Box>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth>
                    <Typography>{t('settings.fixed_rules.dialog.inputs.hours_belong_to')}</Typography>
                    <RadioGroup
                        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
                        aria-labelledby='hours-belong-to-radio-group'
                        name='hours-belong-to-radio-group'
                        row
                        onChange={(e) => form.update('hours_belong_to', e.target.value)}
                        value={formState.hours_belong_to}
                    >
                        <FormControlLabel
                            value={'Dia de entrada planificado'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.planned_entry_day')}
                            disabled={mode === 'info'}
                        />
                        <FormControlLabel
                            value={'Dia de salida planificado'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.planned_departure_day')}
                            disabled={mode === 'info'}
                        />
                        <FormControlLabel
                            value={'Dia con mayoria de horas'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.day_with_most_hours')}
                            disabled={mode === 'info'}
                        />
                        <FormControlLabel
                            value={'Dia realmente trabajado'}
                            control={<Radio />}
                            label={t('settings.fixed_rules.dialog.inputs.day_really_worked')}
                            disabled={mode === 'info'}
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>
        </Grid>
    )
}